// hooks/useDocumentUpload.js
import { useState } from 'react';
import { ragService } from '../ItineraryCreator/rag/ragService';
import { getStoragePathComponents, getCanonicalFolderName } from '../ItineraryCreator/utils/filenameUtils';

export const useDocumentUpload = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);

  /**
   * Upload document file to RAG documents folder
   * @param {File} file - The document file to upload (.txt)
   * @param {Object} metadata - Metadata for the itinerary
   * @returns {Promise<string>} - A promise that resolves to the download URL
   */
  const uploadDocument = async (file, metadata = {}) => {
    if (!file) return null;
    setIsUploading(true);
    setError(null);
    setProgress(0);

    try {
      // Validate file type
      if (!file.name.toLowerCase().endsWith('.json')) {
        throw new Error('Only .json files are allowed for RAG documents');
      }

      // Validate file size (max 1MB for JSON files)
      const maxSizeKB = 1024; // 1MB
      const maxSizeInBytes = maxSizeKB * 1024;
      if (file.size > maxSizeInBytes) {
        throw new Error(`File size exceeds ${maxSizeKB}KB limit. Your file is ${(file.size / 1024).toFixed(1)}KB.`);
      }

      // Create a FormData object to send the file
      const formData = new FormData();
      formData.append('document', file);

      // Validate required metadata
      const name = metadata.name || metadata.itineraryName;
      const language = metadata.language || 'en-us';

      if (!metadata.country || !metadata.filename && !name && !metadata.itineraryName) {
        throw new Error('Country and name are required for document upload');
      }

      // Use the same path generation logic as the rest of the app
      const pathComponents = getStoragePathComponents({
        country: metadata.country,
        name: name,
        filename: metadata.filename,
        language: language
      });

      // Add metadata to specify RAG documents folder
      formData.append('country', metadata.country);
      formData.append('itineraryName', getCanonicalFolderName({
        country: metadata.country,
        name,
        filename: metadata.filename,
        language
      }));

      // Specify the storage path for RAG documents matching manifest structure
      // Path: special_itineraries/{country}/{folderName}/rag_documents/{filename}
      const ragFolderPath = `special_itineraries/${pathComponents.country}/${pathComponents.folderName}/rag_documents/${file.name}`;
      formData.append('storagePath', ragFolderPath);

      setProgress(10);

      // Use the existing backend function but for documents
      const response = await fetch('https://us-central1-guidesofia-access-control.cloudfunctions.net/backendDocumentUploaderToFirebase', {
        method: 'POST',
        body: formData
      });

      setProgress(90);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || 'Failed to upload document');
      }

      const data = await response.json();
      setProgress(100);

      // Convert Firebase Storage REST API URL to direct Cloud Storage URL
      const directUrl = ragService.convertToDirectStorageUrl(data.url);
      return directUrl; // Return the public URL in direct format
    } catch (error) {
      console.error('Error uploading document:', error);
      setError(error.message || 'Failed to upload document');
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  /**
   * Delete a document from Firebase Storage
   * @param {string} documentUrl - The URL or path of the document to delete
   * @param {Object} metadata - Metadata for the itinerary
   * @returns {Promise<boolean>} - Success status
   */
  const deleteDocument = async (documentUrl, metadata = {}) => {
    setIsUploading(true);
    setError(null);

    try {
      const response = await fetch('https://us-central1-guidesofia-access-control.cloudfunctions.net/backendDeleteFileFromFirebase', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileUrl: documentUrl,
          country: metadata.country,
          itineraryName: metadata.itineraryName || getCanonicalFolderName(metadata)
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || 'Failed to delete document');
      }

      return true;
    } catch (error) {
      console.error('Error deleting document:', error);
      setError(error.message || 'Failed to delete document');
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  return {
    uploadDocument,
    deleteDocument,
    isUploading,
    error,
    progress
  };
};
